import { Injectable } from '@angular/core';

import { BaseService } from 'app/_services/base.service';
import { SettingsService } from 'app/_services/settings.service';

import { Comentario } from '../_models/comentario';
import { Pedido } from './_models/pedido';
import { CheckoutAnswer, PurchaseAreaResponse } from './_models/purchaseAreaResponses';
import { TipoCalculoTotalEnum } from './_models/tipoCalculoTotalEnum';
import { Observable } from 'rxjs';
import { BuyPostModel } from './_models/buyPostModel';
import { UserService } from 'app/_services/user.service';

declare const isEmpty: any;
declare const numberWithTwoDecimals: any;

@Injectable({ providedIn: 'root' })
export class BuyerPurchaseService extends BaseService {
    quotationCache = [];
    private _purchaseDomain: string = '/buyer/purchases';
    private _quotationsDomain: string = '/support/quotations';
    private _buyerReviewsDomain: string = '/buyer/reviews';

    constructor(private userService: UserService, private config: SettingsService) {
        super();
    }

    public addComment(comentario: Comentario): Observable<any> {
        const body = { comment: comentario.comentario, order_id: comentario.order_id };
        return this.config.executePost({ url: `${this._purchaseDomain}/${comentario.quotation_id}/comments`, body });
    }

    public buy(model: BuyPostModel): Observable<any> {
        return this.config.executePost({ url: `${this._purchaseDomain}`, body: model });
    }

    public changeQuotation(quotation_id: number, lista_pecas: any, frete: any, supplier_rebate: number): Observable<any> {
        const body = { quotation_id, lista_pecas, frete, supplier_rebate };
        return this.config.executePut({ url: `${this._purchaseDomain}/${quotation_id}/change`, body });
    }

    public getOrderValueFromAnswer(answer: CheckoutAnswer | PurchaseAreaResponse , tipoCalculo: TipoCalculoTotalEnum): any {
        const isPurchaseAreaResponse = answer.hasOwnProperty('pecas');
        const items = isPurchaseAreaResponse ? (answer as PurchaseAreaResponse).pecas : (answer as CheckoutAnswer).parts;
        const shippingCost = isPurchaseAreaResponse
                            ? (isEmpty((answer as PurchaseAreaResponse).shipping_cost_selecionado) ? 0 : (answer as PurchaseAreaResponse).shipping_cost_selecionado)
                            : (isEmpty((answer as CheckoutAnswer).selectedShippingCost) ? 0 : (answer as CheckoutAnswer).selectedShippingCost);

        let total = 0;

        for (let i = 0; i <= items.length - 1; i++) {
            const item: any = items[i];

            const answerQuantity = isPurchaseAreaResponse ? item.answer_quantity : item.answerQuantity;
            const discountOriginal = isPurchaseAreaResponse ? item.discount_original : item.discountOriginal;
            const isChosen = isPurchaseAreaResponse ? item.is_chosen : item.isChosen;

            if (isChosen) {
                total += tipoCalculo === TipoCalculoTotalEnum.TotalOriginal ?
                    numberWithTwoDecimals(answerQuantity * (item.price * (100 - discountOriginal) / 100)) :
                    numberWithTwoDecimals(item.selectedQuantity * (item.price * (100 - item.discount) / 100));
            }
        }

        return {
            total: total + (total > 0 ? shippingCost : 0),
            totalPecas: total,
            frete: shippingCost
        };
    }

    public enviarLinkRapido(quotation_id: number, cnpj: any, empresa: any, vendedor: string, whatsapp: any, seller_company_id: number, ids_pecas: number[]) : Observable<any>{
        const body = { quotation_id, cnpj, empresa, vendedor, whatsapp, seller_company_id, ids_pecas };

        return this.config.executePost({ url: `${this._quotationsDomain}/send-link`, body });
    }

    public deletePhoto(filename: any): Observable<any> {
        const body = { filename };
        return this.config.executeDelete({ url: `${this._purchaseDomain}/image`, body, isJson: false });
    }

    public deletePart(quotation_id: number, item_id: number): Observable<any> {
        return this.config.executeDelete({ url: `${this._purchaseDomain}/${quotation_id}/part/${item_id}` });
    }

    public getComments(quotationId: number): Observable<any> {
        return this.config.executeGet({ url: `${this._purchaseDomain}/${quotationId}/comments` });
    }

    public getChosenAnswers(quotation_id: number): Observable<any> {
        return this.config.executeGet({ url: `${this._purchaseDomain}/${quotation_id}/chosen-ids` });
    }

    public getHeaderInformation(quotationId: number): Observable<any> {
        var companyId = this.userService.companyId();

        return this.config.executeGet({ url: `${this._purchaseDomain}/${quotationId}/header/${companyId}` });
    }

    public getAnswers(quotation_id: number): Observable<any> {
        return this.config.executeGet({ url: `${this._purchaseDomain}/${quotation_id}/answers` });
    }

    public getResponseSellerReview(quotation_id: number, seller_company_id: number): Observable<any> {
        return this.config.executeGet({ url: `${this._buyerReviewsDomain}/seller/${seller_company_id}/quotation/${quotation_id}/detail` });
    }

    public getSellerReviews(seller_company_id: number, page_number: number, page_size: number): Observable<any> {
        return this.config.executeGet({ url: `${this._buyerReviewsDomain}/seller/${seller_company_id}/page/${page_number}/size/${page_size}` });
    }

    public sendChosenAnswersToRepairerManager(quotation_id: number, pedido: Pedido): Observable<any> {
        return this.config.executePost({ url: `${this._purchaseDomain}/${quotation_id}`, body: pedido });
    }

    public setChosenAnswers(quotation_id: number, pecas_selecionadas: any[], isSendQuotation: boolean): Observable<any> {
        const companyId = this.userService.companyId();
        const body = { pecas_selecionadas, companyId, isSendQuotation };
        return this.config.executePost({ url: `${this._purchaseDomain}/${quotation_id}/chosen-ids`, body });
    }

    public uploadQuotationImage(id: any, b64file: string | Blob, filename: string | Blob): Observable<any> {
        var formData = new FormData();
        formData.append("b64file", b64file);
        formData.append("filename", filename);
        formData.append("id", id);

        return this.config.executePost({ url: `${this._purchaseDomain}/image`, body: formData, isJson: false });
    }
}
